import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, IconButton, ListItemIcon,
  ListItemText, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import EditIcon from "@mui/icons-material/Edit";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../../context/AuthContext";
import ComprobanteEditarDialog from "./ComprobanteEditarDialog";
import { EnlazarSolicitudDialog, FacturaDialog, GastoDialog } from "./ComprobanteOtrosDialogs";
import { abrirPdfEnPestana, anularEgreso, enviarCorreoEgreso, fmtFecha, fmtMoney, getComprobante } from "../services/egresosService";

const VERDE = "#0e6b6b";
const colorTitulo = (t) => (t.palette.mode === "dark" ? "#7fd1d1" : VERDE);

// Tarjeta cuadrada con barra de título verde claro y una línea verde debajo (mismo lenguaje que las tablas del módulo).
const Tarjeta = ({ titulo, cantidad, accion, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 0, overflow: "hidden" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.4, bgcolor: "rgba(14,107,107,0.10)", borderBottom: `2px solid ${VERDE}` }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase", color: colorTitulo, flexGrow: 1 }}>{titulo}</Typography>
      {cantidad !== undefined && <Chip size="small" label={cantidad} sx={{ height: 18, fontSize: 10.5, fontWeight: 800, borderRadius: 0 }} />}
      {accion}
    </Box>
    {children}
  </Paper>
);

const cellSx = { py: 0.3, px: 0.8, fontSize: 12 };
const lblSx = {
  ...cellSx, width: "20%", whiteSpace: "nowrap", fontWeight: 800, color: colorTitulo, bgcolor: "rgba(14,107,107,0.10)",
  borderRight: "1px solid", borderColor: "divider",
};

// Fila de la tabla de datos: título coloreado + valor (hasta dos pares por fila).
const Fila = ({ l1, v1, l2, v2, c1, c2 }) => (
  <TableRow>
    <TableCell sx={lblSx}>{l1}</TableCell>
    <TableCell sx={{ ...cellSx, ...c1 }} colSpan={l2 ? 1 : 3}>{v1 || v1 === 0 ? v1 : "-"}</TableCell>
    {l2 && <TableCell sx={lblSx}>{l2}</TableCell>}
    {l2 && <TableCell sx={{ ...cellSx, ...c2 }}>{v2 || v2 === 0 ? v2 : "-"}</TableCell>}
  </TableRow>
);

const vacioTxt = (t) => <Typography sx={{ p: 1.25, fontSize: 12 }} color="text.secondary">{t}</Typography>;

// Comprobante de cheque (espejo de imp_comprobante.php): detalle, solicitud, facturas y gastos, con el menú
// de opciones (imprimir, editar, enlazar con solicitud, enviar correo y anular) y alta / edición de facturas y gastos.
// Con `pagina` se muestra a pantalla completa, sin el diálogo (ruta /egresos/comprobante/:id, que se abre en otra pestaña).
export default function ComprobanteDialog({ id, onClose, onCambio, pagina = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [imprimiendo, setImprimiendo] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editarOpen, setEditarOpen] = useState(false);
  const [enlazarOpen, setEnlazarOpen] = useState(false);
  const [facturaDlg, setFacturaDlg] = useState({ abierto: false, factura: null });
  const [gastoDlg, setGastoDlg] = useState({ abierto: false, gasto: null });

  const cargar = useCallback(() => {
    if (!id) return Promise.resolve();
    return getComprobante(id)
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.error || "No se pudo cargar el comprobante."));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setData(null);
    setError("");
    setOk("");
    cargar();
  }, [id, cargar]);

  // Tras cualquier cambio: recarga el comprobante, muestra el aviso y avisa al listado que lo abrió.
  const tras = (mensaje) => { setOk(mensaje); setError(""); cargar(); if (onCambio) onCambio(); };

  const puedeAnular = ["admin", "gerente"].includes(user?.role);

  const anular = async () => {
    setMenuAnchor(null);
    if (!window.confirm("¿Realmente desea anular el comprobante?")) return;
    try {
      await anularEgreso(id);
      tras("Comprobante anulado.");
    } catch (er) {
      setError(er?.response?.data?.error || "No se pudo anular el comprobante.");
    }
  };

  const enviarCorreo = async () => {
    setMenuAnchor(null);
    if (!window.confirm("¿Enviar el comprobante por correo al solicitante?")) return;
    try {
      const { data: r } = await enviarCorreoEgreso(id);
      setOk(`Correo enviado a ${r.enviado_a}.`);
      setError("");
    } catch (er) {
      setError(er?.response?.data?.error || "No se pudo enviar el correo.");
    }
  };

  const imprimir = async () => {
    setImprimiendo(true);
    try {
      await abrirPdfEnPestana(id);
    } catch {
      setError("No se pudo generar el PDF.");
    } finally {
      setImprimiendo(false);
    }
  };

  const e = data?.egreso;
  const anulado = !!e && e.anulado !== "no";
  const facturasTxt = data ? data.facturas.map((f) => f.nro_factura).filter(Boolean).join(", ") : "";

  const cuerpo = (
    <>
      <Box sx={{ bgcolor: VERDE, color: "#fff", display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: 2, py: 1, ...(pagina && { position: "sticky", top: 0, zIndex: 2 }) }}>
        <ReceiptLongIcon sx={{ fontSize: 28, opacity: 0.9 }} />
        <Box sx={{ lineHeight: 1.15 }}>
          <Typography sx={{ fontSize: 10.5, letterSpacing: 1.5, textTransform: "uppercase", opacity: 0.85 }}>Comprobante de cheque</Typography>
          <Typography sx={{ fontSize: 19, fontWeight: 900 }}>N° {id}</Typography>
        </Box>
        {anulado && <Chip size="small" label="ANULADO" sx={{ height: 22, bgcolor: "#c62828", color: "#fff", fontWeight: 900, letterSpacing: 1, borderRadius: 0 }} />}
        {e && (
          <Box sx={{ lineHeight: 1.2, ml: { md: 2 } }}>
            <Typography sx={{ fontSize: 10.5, opacity: 0.85 }}>Fecha de registro</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{fmtFecha(e.fecha)}</Typography>
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<SettingsIcon />} onClick={(ev) => setMenuAnchor(ev.currentTarget)} disabled={!data}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.7)", borderRadius: 0, "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
          Opciones
        </Button>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem dense disabled={imprimiendo} onClick={() => { setMenuAnchor(null); imprimir(); }}>
            <ListItemIcon>{imprimiendo ? <CircularProgress size={16} /> : <PrintIcon fontSize="small" color="error" />}</ListItemIcon><ListItemText>Imprimir PDF</ListItemText>
          </MenuItem>
          <MenuItem dense onClick={() => { setMenuAnchor(null); setEditarOpen(true); }}>
            <ListItemIcon><EditIcon fontSize="small" color="info" /></ListItemIcon><ListItemText>Editar comprobante</ListItemText>
          </MenuItem>
          {data && !data.solicitud && (
            <MenuItem dense onClick={() => { setMenuAnchor(null); setEnlazarOpen(true); }}>
              <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon><ListItemText>Enlazar con solicitud</ListItemText>
            </MenuItem>
          )}
          {data?.solicitud?.correo && (
            <MenuItem dense onClick={enviarCorreo}>
              <ListItemIcon><MailOutlineIcon fontSize="small" color="warning" /></ListItemIcon><ListItemText>Enviar correo</ListItemText>
            </MenuItem>
          )}
          {puedeAnular && e && e.anulado !== "si" && <Divider />}
          {puedeAnular && e && e.anulado !== "si" && (
            <MenuItem dense onClick={anular} sx={{ color: "error.main" }}>
              <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Anular comprobante</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      <DialogContent sx={{ p: 1.5, bgcolor: "action.hover", ...(pagina && { flex: 1, overflow: "visible" }) }}>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError("")}>{error}</Alert>}
        {ok && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setOk("")}>{ok}</Alert>}
        {!data && !error && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></Box>}
        {data && (
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Tarjeta titulo="Datos del cheque">
                <Table size="small" sx={{ "& td": { borderColor: "divider" } }}>
                  <TableBody>
                    <Fila l1="Fecha de Registro" v1={fmtFecha(e.fecha)} l2="Tipo Cambio" v2={e.cambio} />
                    <Fila l1="Dirigido a" v1={e.nombre} />
                    <Fila l1="Depósito a" v1={e.deposito} />
                    <Fila l1="N° de Cheque" v1={e.nocheque} l2="N° de Contrato" v2={e.nro_contrato} />
                    <Fila l1="Ciudad Destino" v1={e.ciudad} l2="N° de Referencia" v2={e.nro_referencia} />
                    <Fila l1="Banco" v1={e.banco} l2="Tipo Servicio" v2={e.servicio} />
                    <Fila l1="Moneda" v1={e.moneda} l2="Tipo de Egreso" v2={e.tipo_retencion} />
                    <Fila l1="Importe Bs" v1={fmtMoney(e.bolivianos)} l2="Importe USD" v2={fmtMoney(e.dolares)}
                      c1={{ fontWeight: 800, color: "#1565c0" }} c2={{ fontWeight: 800, color: "#2e7d32" }} />
                    <Fila l1="Importe Total" v1={fmtMoney(data.importe_total)} l2="Retención" v2={fmtMoney(e.retencion)} c1={{ fontWeight: 800 }} />
                    <Fila l1="Concepto" v1={e.concepto} />
                    <Fila l1="Mes de Servicio" v1={e.mes} l2="N° de Factura" v2={facturasTxt || "-"} />
                    <Fila l1="Observación" v1={e.obs} />
                    <Fila l1="Literal" v1={data.literal} c1={{ fontStyle: "italic" }} />
                    {data.cliente && <Fila l1="Cliente" v1={data.cliente} />}
                    <Fila l1="Emitido por" v1={e.usu} />
                  </TableBody>
                </Table>
              </Tarjeta>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={1.5}>
                <Tarjeta titulo="Solicitud del cheque">
                  {data.solicitud ? (
                    <Table size="small" sx={{ "& td": { borderColor: "divider" } }}>
                      <TableBody>
                        <Fila l1="Fecha" v1={fmtFecha(data.solicitud.fecha)} l2="Solicitante" v2={data.solicitud.usu} />
                        <Fila l1="Dirigido a" v1={data.solicitud.nombre} />
                        <Fila l1="Depósito a" v1={data.solicitud.deposito} />
                        <Fila l1="Concepto" v1={data.solicitud.concepto} l2="Mes" v2={data.solicitud.mes} />
                      </TableBody>
                    </Table>
                  ) : vacioTxt("No tiene solicitud registrada.")}
                </Tarjeta>

                <Tarjeta titulo="Facturas" cantidad={data.facturas.length} accion={
                  <Tooltip title="Registrar factura"><IconButton size="small" color="primary" onClick={() => setFacturaDlg({ abierto: true, factura: null })} sx={{ p: 0.3 }}><AddIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                }>
                  {data.facturas.length ? (
                    <TableContainer>
                      <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.3, px: 0.7, fontSize: 11 }, "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" } }}>
                        <TableHead>
                          <TableRow>
                            {["Fecha", "N° Factura", "Denominación", "Total", "No sujeto", "Subtotal", "CF", ""].map((h) => (
                              <TableCell key={h} sx={{ fontWeight: 800, color: "text.secondary", fontSize: "10px !important", textTransform: "uppercase" }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.facturas.map((f) => (
                            <TableRow key={f.id}>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(f.fecha_factura)}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{f.nro_factura}</TableCell>
                              <TableCell>{f.denominacion}</TableCell>
                              <TableCell align="right">{fmtMoney(f.importe_total)}</TableCell>
                              <TableCell align="right">{fmtMoney(f.importe_no_sujeto)}</TableCell>
                              <TableCell align="right">{fmtMoney(f.subtotal)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(f.credito_fiscal)}</TableCell>
                              <TableCell>
                                <Tooltip title="Editar factura"><IconButton size="small" color="primary" sx={{ p: 0.2 }} onClick={() => setFacturaDlg({ abierto: true, factura: f })}><EditIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : vacioTxt("No se encontraron facturas en este comprobante.")}
                </Tarjeta>

                <Tarjeta titulo="Otros gastos" cantidad={data.gastos.length} accion={
                  <Tooltip title="Registrar gasto"><IconButton size="small" color="primary" onClick={() => setGastoDlg({ abierto: true, gasto: null })} sx={{ p: 0.3 }}><AddIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                }>
                  {data.gastos.length ? (
                    <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.3, px: 0.7, fontSize: 11 }, "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" } }}>
                      <TableHead>
                        <TableRow>
                          {["Detalle", "Monto Bs", "Fecha", "Registrado por", ""].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 800, color: "text.secondary", fontSize: "10px !important", textTransform: "uppercase" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.gastos.map((g) => (
                          <TableRow key={g.id}>
                            <TableCell>{g.detalle}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(g.monto)}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(g.fecha)}</TableCell>
                            <TableCell>{g.usuario}</TableCell>
                            <TableCell>
                              <Tooltip title="Editar gasto"><IconButton size="small" color="primary" sx={{ p: 0.2 }} onClick={() => setGastoDlg({ abierto: true, gasto: g })}><EditIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : vacioTxt("No existen registros de gastos.")}
                </Tarjeta>
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      {!pagina && (
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={onClose} sx={{ borderRadius: 0 }}>Cerrar</Button>
        </DialogActions>
      )}

      <ComprobanteEditarDialog abierto={editarOpen} data={data} onClose={() => setEditarOpen(false)} onGuardado={tras} />
      <EnlazarSolicitudDialog abierto={enlazarOpen} egresoId={id} onClose={() => setEnlazarOpen(false)} onGuardado={tras} />
      <FacturaDialog abierto={facturaDlg.abierto} egresoId={id} factura={facturaDlg.factura} onClose={() => setFacturaDlg((p) => ({ ...p, abierto: false }))} onGuardado={tras} />
      <GastoDialog abierto={gastoDlg.abierto} egresoId={id} gasto={gastoDlg.gasto} onClose={() => setGastoDlg((p) => ({ ...p, abierto: false }))} onGuardado={tras} />
    </>
  );

  if (pagina) {
    return <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>{cuerpo}</Box>;
  }
  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="lg" fullWidth slotProps={{ paper: { sx: { borderRadius: 0 } } }}>
      {cuerpo}
    </Dialog>
  );
}
